import React, { useEffect, useState } from 'react';
import SafePlyr from './SafePlyr';
import LoadingSpinner from './LoadingSpinner';
import '../styles/global.css';

import localAudios from '../data/localAudios.json';
import { useAuth } from '../hooks/useAuth';
import {
    INSTITUCION_SEMAR,
    getInstitutionLabel,
    resolveInstitucion,
} from '../config/institutions';
import { listR2AudioObjects, r2PublicUrl } from '../services/mediaSigner';

// Prefijos del bucket `reels` en Cloudflare R2. Cada carpeta interna agrupa
// los audios de un área o asignatura de la institución.
const R2_AUDIO_PREFIX = {
    GUARDIA_NACIONAL: 'AUDIOS/audiosGN/',
    SEDENA: 'AUDIOS/audiosSEDENA/',
};

const AUDIO_EXT_RE = /\.(mp3|m4a|aac|ogg|wav|flac)$/i;

const prettyName = (raw) => String(raw || '')
    .replace(/\.(mp3|m4a|aac|ogg|wav|flac)$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const fileNameOf = (key) => String(key || '').split('/').pop() || key;

const fmtSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) { value /= 1024; i++; }
    return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[i]}`;
};

const listAllObjects = async ({ prefix, delimiter }) => {
    const folders = [];
    const objects = [];
    let cursor = '';
    do {
        const data = await listR2AudioObjects({ prefix, delimiter, cursor, max: 1000 });
        folders.push(...(data.folders || []));
        objects.push(...(data.objects || []));
        cursor = data.next || '';
    } while (cursor);
    return { folders, objects };
};

const AudioLibrary = () => {
    const { institucion: authInstitucion, profileLoading } = useAuth();
    const institucion = resolveInstitucion(authInstitucion);
    const isSemar = institucion === INSTITUCION_SEMAR;
    const r2Prefix = R2_AUDIO_PREFIX[institucion] || '';

    const [audios] = useState(localAudios);
    const [currentAudio, setCurrentAudio] = useState(null);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading] = useState(false);

    const [r2Folders, setR2Folders] = useState([]);
    const [r2Audios, setR2Audios] = useState([]);
    const [r2Loading, setR2Loading] = useState(false);
    const [r2Error, setR2Error] = useState('');
    const [reloadToken, setReloadToken] = useState(0);

    // Carpetas de la institución (nivel raíz del prefijo R2).
    useEffect(() => {
        if (profileLoading || isSemar) return;
        if (!r2Prefix) {
            setR2Error('Esta institución aún no tiene una carpeta de audios configurada.');
            return;
        }
        let cancelled = false;
        setR2Folders([]);
        setR2Audios([]);
        setR2Loading(true);
        setR2Error('');

        listAllObjects({ prefix: r2Prefix, delimiter: '/' })
            .then(({ folders }) => {
                if (cancelled) return;
                const entries = folders
                    .map((prefix) => ({
                        id: prefix,
                        label: prettyName(prefix.slice(r2Prefix.length).replace(/\/$/, '')),
                    }))
                    .filter((entry) => entry.label)
                    .sort((a, b) => a.label.localeCompare(b.label, 'es', { numeric: true }));
                setR2Folders(entries);
            })
            .catch((error) => {
                if (!cancelled) setR2Error(error?.message || 'No se pudieron cargar las carpetas de audio.');
            })
            .finally(() => {
                if (!cancelled) setR2Loading(false);
            });

        return () => { cancelled = true; };
    }, [profileLoading, isSemar, r2Prefix, reloadToken]);

    // Audios dentro de la carpeta seleccionada, con URLs firmadas de corta duración.
    useEffect(() => {
        if (isSemar || !selectedFolder) return;
        let cancelled = false;
        setR2Audios([]);
        setR2Loading(true);
        setR2Error('');

        listAllObjects({ prefix: selectedFolder, delimiter: '' })
            .then(({ objects }) => {
                if (cancelled) return;
                setR2Audios(objects
                    .filter((object) => AUDIO_EXT_RE.test(object.key))
                    .map((object) => ({
                        id: object.key,
                        key: object.key,
                        title: prettyName(fileNameOf(object.key)),
                        size: object.size,
                        url: r2PublicUrl(object.key),
                    }))
                    .sort((a, b) => a.title.localeCompare(b.title, 'es', { numeric: true })));
            })
            .catch((error) => {
                if (!cancelled) setR2Error(error?.message || 'No se pudieron cargar los audios de la carpeta.');
            })
            .finally(() => {
                if (!cancelled) setR2Loading(false);
            });

        return () => { cancelled = true; };
    }, [isSemar, selectedFolder, reloadToken]);

    const handlePlay = (audio) => {
        setCurrentAudio(audio);
    };

    const handleDownload = (event, audio) => {
        event.stopPropagation();
        event.preventDefault();
        const link = document.createElement('a');
        link.href = audio.url;
        link.download = `${audio.title}.${String(audio.key || audio.url).split('.').pop()}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const semarFolders = [...new Set(audios.map(a => a.area || 'General'))].sort();
    const folderEntries = isSemar
        ? semarFolders.map((name) => ({
            id: name,
            label: name,
            count: audios.filter(a => (a.area || 'General') === name).length,
        }))
        : r2Folders;
    const selectedFolderLabel = isSemar
        ? selectedFolder
        : (r2Folders.find(folder => folder.id === selectedFolder)?.label || '');

    const displayedAudios = isSemar
        ? (selectedFolder ? audios.filter(a => (a.area || 'General') === selectedFolder) : [])
        : r2Audios;

    const sortNatural = (arr) => [...arr].sort((a, b) => {
        const titleA = a.title || a.nombre || '';
        const titleB = b.title || b.nombre || '';
        const numA = parseFloat(titleA.match(/^[\d.]+/)?.[0] || titleA);
        const numB = parseFloat(titleB.match(/^[\d.]+/)?.[0] || titleB);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return titleA.localeCompare(titleB, undefined, { numeric: true });
    });

    const finalFilteredAudios = sortNatural(displayedAudios.filter(a =>
        (a.title || a.nombre || '').toLowerCase().includes(searchTerm.toLowerCase())
    ));

    // Evita mostrar la biblioteca local (SEMAR) mientras se resuelve la institución.
    if (profileLoading) return (
        <div style={{ padding: '40px' }}>
            <LoadingSpinner label="Cargando biblioteca..." height="240px" />
        </div>
    );

    if (isSemar && loading) return (
        <div style={{ padding: '20px', color: 'var(--text-primary)', textAlign: 'center' }}>
            <div className="loading-spinner"></div>
            <p>Cargando biblioteca de audio...</p>
            <LoadingSpinner label="Cargando datos..." height="300px" />
        </div>
    );

    const emptyMessage = isSemar
        ? 'No se encontraron audios en esta unidad.'
        : 'No se encontraron audios en esta carpeta.';

    return (
        <div className="audio-library" style={{ padding: '20px', color: 'var(--text-primary)', paddingBottom: '100px' }}>
            <div className="flex-between" style={{ marginBottom: '20px', alignItems: 'center' }}>
                <div>
                    <h1 style={{ color: 'var(--accent)', margin: 0, fontSize: '2.5rem', fontWeight: '800' }}>
                        Repositorio de Audio 🎧
                    </h1>
                    <p style={{ opacity: 0.6, marginTop: '5px' }}>
                        {selectedFolder
                            ? `Carpeta: ${selectedFolderLabel}`
                            : (isSemar
                                ? 'Seleccione una unidad temática estratégica'
                                : `Audios de ${getInstitutionLabel(institucion)}`)}
                    </p>
                </div>
                {selectedFolder && (
                    <button
                        onClick={() => { setSelectedFolder(null); setSearchTerm(''); }}
                        className="btn-secondary"
                        style={{ padding: '8px 16px', borderRadius: '12px' }}
                    >
                        ⬅ Volver a Carpetas
                    </button>
                )}
            </div>

            {r2Error && (
                <div className="card" style={{ textAlign: 'center', padding: '30px', marginBottom: '20px' }}>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '15px' }}>⚠️ {r2Error}</p>
                    <button
                        className="btn-secondary"
                        style={{ padding: '8px 20px', borderRadius: '12px' }}
                        onClick={() => setReloadToken(token => token + 1)}
                    >
                        Reintentar
                    </button>
                </div>
            )}

            {/* FOLDER SELECTION VIEW */}
            {!selectedFolder && !r2Error && (
                r2Loading && !isSemar ? (
                    <div style={{ padding: '40px' }}>
                        <LoadingSpinner label="Cargando carpetas..." height="240px" />
                    </div>
                ) : (
                    <div className="folder-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '25px',
                        marginTop: '30px'
                    }}>
                        {folderEntries.length === 0 && !r2Loading && (
                            <div className="card" style={{ textAlign: 'center', padding: '40px', gridColumn: '1 / -1' }}>
                                <p style={{ color: 'var(--text-muted)' }}>Aún no hay audios disponibles para tu institución.</p>
                            </div>
                        )}
                        {folderEntries.map(entry => (
                            <div
                                key={entry.id}
                                className="card fold-card"
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedFolder(entry.id)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedFolder(entry.id); } }}
                                style={{
                                    padding: '30px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: 'var(--glass-bg)',
                                    border: '1px solid var(--glass-border)',
                                    borderRadius: '24px',
                                    transition: 'transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-10px)';
                                    e.currentTarget.style.borderColor = 'var(--accent)';
                                    e.currentTarget.style.boxShadow = 'var(--neon-glow)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'none';
                                    e.currentTarget.style.borderColor = 'var(--glass-border)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div style={{ fontSize: '4rem', marginBottom: '15px' }}>📂</div>
                                <h3 style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '8px' }}>{entry.label}</h3>
                                <span style={{
                                    background: 'rgba(0, 242, 254, 0.1)',
                                    color: 'var(--accent)',
                                    padding: '4px 12px',
                                    borderRadius: '50px',
                                    fontSize: '0.85rem',
                                    fontWeight: '600'
                                }}>
                                    {isSemar
                                        ? `${entry.count} ${entry.count === 1 ? 'Audio' : 'Audios'}`
                                        : 'Carpeta'}
                                </span>
                            </div>
                        ))}
                    </div>
                )
            )}

            {/* SEARCH AND AUDIO LIST VIEW */}
            {selectedFolder && !r2Error && (
                <>
                    <div className="search-container" style={{ marginBottom: '30px' }}>
                        <input
                            type="text"
                            placeholder={`🔍 Buscar en ${selectedFolderLabel}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="form-input"
                            style={{
                                width: '100%',
                                padding: '15px 25px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                color: 'white',
                                border: '1px solid var(--glass-border)',
                                borderRadius: '50px',
                                outline: 'none'
                            }}
                        />
                    </div>

                    {r2Loading && !isSemar ? (
                        <div style={{ padding: '40px' }}>
                            <LoadingSpinner label="Cargando audios..." height="240px" />
                        </div>
                    ) : (
                        <div className="audio-list" style={{ display: 'grid', gap: '12px' }}>
                            {finalFilteredAudios.length === 0 ? (
                                <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                                    <p style={{ color: 'var(--text-muted)' }}>{emptyMessage}</p>
                                </div>
                            ) : (
                                finalFilteredAudios.map(audio => (
                                    <div
                                        key={audio.id}
                                        className={`audio-item ${currentAudio?.id === audio.id ? 'active' : ''}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '15px 20px',
                                            background: currentAudio?.id === audio.id
                                                ? 'linear-gradient(90deg, rgba(0, 242, 254, 0.15) 0%, rgba(0, 210, 235, 0.05) 100%)'
                                                : 'var(--glass-bg)',
                                            border: currentAudio?.id === audio.id ? '1px solid var(--accent)' : '1px solid var(--glass-border)',
                                            borderRadius: '16px',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s ease, border-color 0.2s ease'
                                        }}
                                        onClick={() => handlePlay(audio)}
                                    >
                                        <div style={{
                                            width: '44px',
                                            height: '44px',
                                            background: currentAudio?.id === audio.id ? 'var(--accent)' : 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '15px',
                                            fontSize: '1.2rem',
                                            color: currentAudio?.id === audio.id ? '#0a0e17' : 'white'
                                        }}>
                                            {currentAudio?.id === audio.id ? '🔊' : '▶️'}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <h3 style={{ fontSize: '1rem', margin: 0, fontWeight: '600', color: currentAudio?.id === audio.id ? 'var(--accent)' : 'white' }}>
                                                {audio.title || audio.nombre}
                                            </h3>
                                            <span style={{ fontSize: '0.75rem', opacity: 0.5 }}>
                                                {audio.size ? fmtSize(audio.size) : 'Fragmento Educativo Vortex'}
                                            </span>
                                        </div>
                                        {!isSemar && (
                                            <button
                                                type="button"
                                                onClick={(event) => handleDownload(event, audio)}
                                                title="Descargar audio"
                                                aria-label={`Descargar ${audio.title}`}
                                                style={{
                                                    background: 'none',
                                                    border: '1px solid var(--glass-border)',
                                                    color: 'var(--text-primary)',
                                                    borderRadius: '10px',
                                                    width: '40px',
                                                    height: '40px',
                                                    cursor: 'pointer',
                                                    fontSize: '1rem',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                ⬇️
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </>
            )}

            {/* PLYR PLAYER FIXED BOTTOM */}
            {currentAudio && (
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: '#1a1b26', // Dark background specifically for audio
                    padding: '20px',
                    borderTop: '1px solid var(--accent)',
                    zIndex: 2000,
                    boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
                    animation: 'slideUp 0.3s ease-out'
                }}>
                    <div style={{
                        maxWidth: '900px',
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '5px'
                        }}>
                            <span style={{ color: 'white', fontWeight: '600' }}>
                                🎵 Reproduciendo: <span style={{ color: 'var(--accent)' }}>{currentAudio.title || currentAudio.nombre}</span>
                            </span>
                            <button
                                onClick={() => setCurrentAudio(null)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#666',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem'
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="plyr-wrapper" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                            <SafePlyr
                                source={{
                                    type: 'audio',
                                    sources: [
                                        {
                                            src: currentAudio.url || currentAudio.dropboxUrl,
                                            // Detectar MIME type dinámicamente según extensión del archivo
                                            type: (() => {
                                                const url = currentAudio.url || currentAudio.dropboxUrl || '';
                                                const ext = url.split('.').pop().toLowerCase().split('?')[0];
                                                const mimeMap = {
                                                    'aac': 'audio/aac',
                                                    'acc': 'audio/aac',
                                                    'mp3': 'audio/mpeg',
                                                    'ogg': 'audio/ogg',
                                                    'wav': 'audio/wav',
                                                    'm4a': 'audio/mp4',
                                                    'flac': 'audio/flac',
                                                };
                                                return mimeMap[ext] || 'audio/mpeg';
                                            })(),
                                        },
                                    ],
                                }}
                                options={{
                                    autoplay: true,
                                    controls: [
                                        'play-large', 'restart', 'rewind', 'play', 'fast-forward',
                                        'progress', 'current-time', 'duration', 'mute', 'volume',
                                        'captions', 'settings', 'pip', 'airplay', 'download'
                                    ],
                                    speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] }
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                
                /* Custom Plyr Theme adjustments */
                :root {
                    --plyr-color-main: var(--accent);
                }
                .plyr--audio .plyr__controls {
                    background: transparent;
                    color: white;
                }
                .plyr--audio .plyr__control--overlaid-pause, 
                .plyr--audio .plyr__control--overlaid-play {
                    background: var(--accent);
                }

                @media (max-width: 640px) {
                    .folder-grid {
                        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)) !important;
                        gap: 12px !important;
                        margin-top: 16px !important;
                    }
                    .fold-card {
                        padding: 16px !important;
                        border-radius: 12px !important;
                    }
                    .fold-card div:first-child {
                        font-size: 2rem !important;
                        margin-bottom: 8px !important;
                    }
                    .fold-card h3 {
                        font-size: 0.75rem !important;
                        margin-bottom: 4px !important;
                    }
                    .fold-card span {
                        font-size: 0.6rem !important;
                        padding: 2px 8px !important;
                    }
                    .audio-library h1 {
                        font-size: 1.3rem !important;
                    }
                    .audio-library .flex-between {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 8px !important;
                    }
                    .audio-item {
                        padding: 10px 12px !important;
                        border-radius: 10px !important;
                    }
                    .audio-item div:first-child {
                        width: 32px !important;
                        height: 32px !important;
                        font-size: 0.9rem !important;
                        margin-right: 10px !important;
                    }
                    .audio-item h3 {
                        font-size: 0.8rem !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default AudioLibrary;
